import React from "react";
import { IconButton, ToggleButton } from "@mui/material";
import { ExpressionRuleEditor } from "../ExpressionRuleEditor";
import {
    IfThenExpression,
    IfThenPair,
    IfThenStyle,
} from "../../../rule-engine-core/Expressions/Expressions/Logic/IfThenExpression";
import { AutoBind } from "../../../rule-engine-core/Common/AutoBind";
import { ExpressionLineDiv, RuleChangeToggleButtonGroup } from "../../Builder/ExpressionStyles";
import { ExpressionReactContext, ExpressionRenderType } from "../../Structure/ExpressionReactContext";
import { TestContext } from "../../../CommonUI/Testing/TestContext";
import { ExpressionTestResultColorContext } from "../../Testing/ExpressionTestResultColorContext";
import { ExpressionValueRenderer } from "../../Builder/ExpressionValueRenderer";
import { StateManager } from "../../../rule-engine-core/Common/StateManager";
import { InlineTextEdit } from "../../../CommonUI/InlineEditor/InlineTextEdit";
import { PlusIcon } from "../../../CommonUI/Icon/PlusIcon";
import { DeleteIcon } from "../../../CommonUI/Icon/DeleteIcon";
import { ExpressionValue } from "../../../rule-engine-core/Expressions/ExpressionValue";
import { BaseTestResultData } from "../../../rule-engine-core/Common/Testing/BaseTestResultData";
import { TestingConstants } from "../../../CommonUI/Testing/TestingConstants";
import { ObserveState } from "../../../CommonUI/StateManagement/ObserveState";
import { RenderExpressionRule } from "../../Utilities/RegisterExpressionRuleEditor";
import styled from "styled-components";
import { DataTypes } from "../../../rule-engine-core/Common/Data/DataTypes";
import { LeftAlignIcon } from "../../../CommonUI/Icon/LeftAlignIcon";
import { ActiveLeftAlignIcon } from "../../../CommonUI/Icon/ActiveLeftAlignIcon";
import { RightAlignIcon } from "../../../CommonUI/Icon/RightAlignIcon";
import { ActiveRightAlignIcon } from "../../../CommonUI/Icon/ActiveRightAlignIcon";
import { LogicalIcon } from "../../../CommonUI/Icon/LogicalIcon";
import { ActiveLogicalIcon } from "../../../CommonUI/Icon/ActiveLogicalIcon";
import { UIStyleConstants } from "../../../rule-engine-core/Common/UIStyleConstants";
import type { AppGlueAny } from "../../../rule-engine-core/Common/AppGlueAny";

export class IfThenExpressionEditor extends ExpressionRuleEditor<IfThenExpression> {
    @AutoBind
    renderEnd(): JSX.Element {
        if (this.ruleData!.ifThenStyle === IfThenStyle.LOGICAL_RETURN && this.ruleData!.showSimpleReturn) {
            return (
                <IfSection>
                    <ExpressionLineDiv>{this.renderThen()}</ExpressionLineDiv>
                    <ExpressionReactContext.Consumer>
                        {(context) => (
                            <TestContext.Consumer>
                                {(testcontext) => (
                                    <IfIndentedSection>
                                        <ExpressionLineDiv color={this.getLogicDataTestColor(testcontext, "true")}>
                                            <ResultSection
                                                bracketcolor={this.getLogicDataTestColor(testcontext, "true")}
                                                renderType={context.renderType}
                                            >
                                                &nbsp;&nbsp;return true&nbsp;&nbsp;
                                            </ResultSection>
                                        </ExpressionLineDiv>

                                        <ExpressionLineDiv>else</ExpressionLineDiv>
                                        <ExpressionLineDiv color={this.getLogicDataTestColor(testcontext, "false")}>
                                            <ResultSection
                                                bracketcolor={this.getLogicDataTestColor(testcontext, "false")}
                                                renderType={context.renderType}
                                            >
                                                &nbsp;&nbsp;return false&nbsp;&nbsp;
                                            </ResultSection>
                                        </ExpressionLineDiv>
                                    </IfIndentedSection>
                                )}
                            </TestContext.Consumer>
                        )}
                    </ExpressionReactContext.Consumer>
                </IfSection>
            );
        }

        if (this.ruleData!.ifThenStyle === IfThenStyle.BRANCH_DATA_RETURN) {
            if (this.ruleData!.showElseAsDefault) {
                return (
                    <TestContext.Consumer>
                        {(testcontext) => (
                            <ExpressionTestResultColorContext.Provider
                                value={this.getTestValueColor(this.ruleData!.defaultOutput, testcontext)}
                            >
                                <IfSection>
                                    <ExpressionLineDiv />
                                    <ExpressionLineDiv>
                                        {this.ruleData!.defaultText} ={" "}
                                        <ExpressionValueRenderer el={this.ruleData!.defaultOutput} />
                                    </ExpressionLineDiv>
                                </IfSection>
                            </ExpressionTestResultColorContext.Provider>
                        )}
                    </TestContext.Consumer>
                );
            } else {
                return (
                    <TestContext.Consumer>
                        {(testcontext) => (
                            <ExpressionTestResultColorContext.Provider
                                value={this.getTestValueColor(this.ruleData!.defaultOutput, testcontext)}
                            >
                                <IfSection>
                                    <ExpressionLineDiv>else</ExpressionLineDiv>
                                    <IfIndentedSection>
                                        <ExpressionLineDiv>{this.renderThen()}</ExpressionLineDiv>
                                        <ExpressionLineDiv>
                                            <ExpressionValueRenderer el={this.ruleData!.defaultOutput} />
                                        </ExpressionLineDiv>
                                    </IfIndentedSection>
                                </IfSection>
                            </ExpressionTestResultColorContext.Provider>
                        )}
                    </TestContext.Consumer>
                );
            }
        }

        if (this.ruleData!.ifThenStyle === IfThenStyle.DATA_RETURN) {
            return (
                <TestContext.Consumer>
                    {(testcontext) => (
                        <ExpressionReactContext.Consumer>
                            {(context) => (
                                <IfSection>
                                    <IfIndentedSection>
                                        <ExpressionLineDiv>{this.renderThen()}</ExpressionLineDiv>
                                        <ExpressionLineDiv>
                                            &nbsp;&nbsp;
                                            <ExpressionTestResultColorContext.Provider
                                                value={this.getTestValueColor(this.ruleData!.trueOutput, testcontext)}
                                            >
                                                <ResultSection
                                                    bracketcolor={this.getTestValueColor(
                                                        this.ruleData!.trueOutput,
                                                        testcontext,
                                                    )}
                                                    renderType={context.renderType}
                                                >
                                                    &nbsp;&nbsp;
                                                    <ExpressionValueRenderer el={this.ruleData!.trueOutput} />
                                                </ResultSection>
                                            </ExpressionTestResultColorContext.Provider>
                                        </ExpressionLineDiv>
                                        <ExpressionLineDiv>else</ExpressionLineDiv>
                                        <ExpressionTestResultColorContext.Provider
                                            value={this.getTestValueColor(this.ruleData!.defaultOutput, testcontext)}
                                        >
                                            <ExpressionLineDiv>
                                                &nbsp;&nbsp;
                                                <ResultSection
                                                    bracketcolor={this.getTestValueColor(
                                                        this.ruleData!.defaultOutput,
                                                        testcontext,
                                                    )}
                                                    renderType={context.renderType}
                                                >
                                                    <ExpressionValueRenderer el={this.ruleData!.defaultOutput} />
                                                </ResultSection>
                                            </ExpressionLineDiv>
                                        </ExpressionTestResultColorContext.Provider>
                                    </IfIndentedSection>
                                </IfSection>
                            )}
                        </ExpressionReactContext.Consumer>
                    )}
                </TestContext.Consumer>
            );
        }

        return <ExpressionLineDiv />;
    }

    @AutoBind
    renderThen() {
        return (
            <ExpressionReactContext.Consumer>
                {(context) => (
                    <InlineTextEdit
                        text={this.ruleData!.thenText}
                        onEdit={(newVal: string) => {
                            this.ruleData!.thenText = newVal;
                            this.ruleData!.expressionContext?.refresh();
                        }}
                        readOnly={
                            context.renderType === ExpressionRenderType.COMPACT ||
                            context.renderType === ExpressionRenderType.TEXT
                        }
                    />
                )}
            </ExpressionReactContext.Consumer>
        );
    }

    @AutoBind
    renderBranchActions(index: number, renderRuleChange: boolean = false) {
        return (
            <TestContext.Consumer>
                {(testcontext) => {
                    if (testcontext) return null;

                    let isLast = index + 1 === this.ruleData!._ifBranches.length;
                    if (isLast) {
                        if (index === 0) {
                            if (renderRuleChange) {
                                return (
                                    <FloatRight>
                                        {this.renderDeleteBranchButton(index)}
                                        <IconButton
                                            size={"small"}
                                            onClick={this.addIfBranchFromAction}
                                        >
                                            <PlusIcon />
                                        </IconButton>
                                        &nbsp;
                                        {this.renderRuleChange()}
                                    </FloatRight>
                                );
                            } else {
                                return (
                                    <ActionButtons>
                                        {this.renderDeleteBranchButton(index)}
                                        <IconButton
                                            size={"small"}
                                            onClick={this.addIfBranchFromAction}
                                        >
                                            <PlusIcon />
                                        </IconButton>
                                    </ActionButtons>
                                );
                            }
                        } else {
                            return (
                                <ActionButtons>
                                    {this.renderDeleteBranchButton(index)}
                                    <IconButton
                                        size={"small"}
                                        onClick={this.addIfBranchFromAction}
                                    >
                                        <PlusIcon />
                                    </IconButton>
                                </ActionButtons>
                            );
                        }
                    } else if (index !== 0) {
                        return (
                            <ActionButtons>
                                {this.renderDeleteBranchButton(index)}
                            </ActionButtons>
                        );
                    } else if (renderRuleChange) {
                        return (
                            <FloatRight>
                                {this.renderDeleteBranchButton(index)}
                                {this.renderRuleChange()}
                            </FloatRight>
                        );
                    } else {
                        return <ActionButtons>{this.renderDeleteBranchButton(index)}</ActionButtons>;
                    }
                }}
            </TestContext.Consumer>
        );
    }

    private renderDeleteBranchButton(index: number) {
        return (
            <IconButton
                size={"small"}
                aria-label="delete"
                onClick={(event: React.MouseEvent<HTMLElement>) => {
                    event.stopPropagation();
                    this.deleteIfBranchFromAction(index);
                }}
            >
                <DeleteIcon />
            </IconButton>
        );
    }

    getTestValueColor(el: ExpressionValue, testcontext: BaseTestResultData | undefined): string | undefined {
        if (!testcontext) return undefined;

        if (!testcontext) return undefined;
        let testitem = testcontext.findItem(el._id);

        if (testitem) {
            return TestingConstants.TEXT_SUCCESS;
        }

        return undefined;
    }

    getLogicDataTestColor(testcontext: BaseTestResultData | undefined, val: string): string | undefined {
        if (!testcontext) return undefined;

        if (!testcontext) return undefined;
        let testitem = testcontext.findItem(this.ruleData!._id);

        if (testitem?.result === val) return UIStyleConstants.GREEN_COLOR;
        if (testitem?.result !== val) return UIStyleConstants.RED_COLOR;

        return undefined;
    }

    checkPairPassedInTest(pairId: string, testcontext: BaseTestResultData | undefined): boolean | null {
        if (!testcontext) return null;
        let testitem = testcontext.findItem(pairId);
        if (!testitem) return null;

        if (testitem.result === "true") return true;

        if (testitem.result === "false") return false;

        return null;
    }

    getPairTestValueColor(pairId: string, testcontext: BaseTestResultData | undefined): string | undefined {
        let result = this.checkPairPassedInTest(pairId, testcontext);

        if (result === null) return undefined;

        let color: string | undefined = result ? TestingConstants.TEXT_SUCCESS : TestingConstants.TEXT_FAIL;

        return color;
    }

    renderPairTestResult(pairId: string, testcontext: BaseTestResultData | undefined): JSX.Element | null {
        let result = this.checkPairPassedInTest(pairId, testcontext);

        let color: string | undefined = result ? TestingConstants.TEXT_SUCCESS : TestingConstants.TEXT_FAIL;

        if (result === null) return null;

        return <TestValueRender color={color}>{result.toString()}</TestValueRender>;
    }

    render() {
        return (
            <ObserveState
                listenTo={this.ruleData}
                control={() => (
                    <TestContext.Consumer>
                        {(testcontext) => (
                            <AllIfDiv>
                                {this.ruleData!._ifBranches.map((value: IfThenPair, index: number) => {
                                    if (this.ruleData!.ifThenStyle === IfThenStyle.BRANCH_DATA_RETURN) {
                                        if (index === 0) {
                                            return (
                                                <ExpressionTestResultColorContext.Provider
                                                    value={this.getPairTestValueColor(value._id, testcontext)}
                                                    key={value._id}
                                                >
                                                    <IfSection key={value._id}>
                                                        <ExpressionLineDiv>
                                                            if {RenderExpressionRule(value.ifExpression)}
                                                            {this.renderPairTestResult(value._id, testcontext)}
                                                            <FloatRight>{this.renderRuleChange()}</FloatRight>
                                                        </ExpressionLineDiv>

                                                        <IfIndentedSection>
                                                            <ExpressionLineDiv>{this.renderThen()}</ExpressionLineDiv>
                                                            <ExpressionLineDiv>
                                                                &nbsp;&nbsp;
                                                                <ExpressionTestResultColorContext.Provider
                                                                    value={undefined}
                                                                >
                                                                    <ExpressionValueRenderer
                                                                        el={value.thenExpression!}
                                                                    />
                                                                </ExpressionTestResultColorContext.Provider>
                                                                {this.renderBranchActions(index)}
                                                            </ExpressionLineDiv>
                                                        </IfIndentedSection>
                                                    </IfSection>
                                                </ExpressionTestResultColorContext.Provider>
                                            );
                                        } else {
                                            return (
                                                <ExpressionTestResultColorContext.Provider
                                                    value={this.getPairTestValueColor(value._id, testcontext)}
                                                    key={value._id}
                                                >
                                                    <IfSection key={value._id}>
                                                        <ExpressionLineDiv>
                                                            else if {RenderExpressionRule(value.ifExpression)}
                                                            {this.renderBranchActions(index)}
                                                            {this.renderPairTestResult(value._id, testcontext)}
                                                        </ExpressionLineDiv>

                                                        <IfIndentedSection>
                                                            <ExpressionLineDiv>{this.renderThen()}</ExpressionLineDiv>
                                                            <ExpressionLineDiv>
                                                                &nbsp;&nbsp;
                                                                <ExpressionValueRenderer el={value.thenExpression!} />
                                                            </ExpressionLineDiv>
                                                        </IfIndentedSection>
                                                    </IfSection>
                                                </ExpressionTestResultColorContext.Provider>
                                            );
                                        }
                                    } else {
                                        if (index === 0) {
                                            return (
                                                <ExpressionTestResultColorContext.Provider
                                                    value={this.getPairTestValueColor(value._id, testcontext)}
                                                    key={value._id}
                                                >
                                                    <IfSection key={value._id}>
                                                        <ExpressionLineDiv>
                                                            if {RenderExpressionRule(value.ifExpression)}
                                                            {this.renderPairTestResult(value._id, testcontext)}
                                                            {this.renderBranchActions(index, true)}
                                                        </ExpressionLineDiv>
                                                    </IfSection>
                                                </ExpressionTestResultColorContext.Provider>
                                            );
                                        } else {
                                            return (
                                                <ExpressionTestResultColorContext.Provider
                                                    value={this.getPairTestValueColor(value._id, testcontext)}
                                                    key={value._id}
                                                >
                                                    <IfSection key={value._id}>
                                                        <ExpressionLineDiv>
                                                            else if {RenderExpressionRule(value.ifExpression)}
                                                            {this.renderBranchActions(index)}
                                                            {this.renderPairTestResult(value._id, testcontext)}
                                                        </ExpressionLineDiv>
                                                    </IfSection>
                                                </ExpressionTestResultColorContext.Provider>
                                            );
                                        }
                                    }
                                })}
                                {this.renderEnd()}
                            </AllIfDiv>
                        )}
                    </TestContext.Consumer>
                )}
            />
        );
    }

    @AutoBind
    private addIfBranchFromAction(event: React.MouseEvent<HTMLElement>) {
        event.stopPropagation();
        this.ruleData!.addIfBranch();
    }

    @AutoBind
    private deleteIfBranchFromAction(index: number) {
        if (this.ruleData!.ifBranches.length === 1) {
            const parentExpressionValue = this.ruleData!.expressionContext?.getParentExpressionValue(
                this.ruleData!._id,
            );

            if (parentExpressionValue) {
                parentExpressionValue.clear();
                return;
            }
        }

        this.ruleData!.deleteIfBranch(index);
    }

    @AutoBind
    private renderRuleChange() {
        return (
            <TestContext.Consumer>
                {(testcontext) => {
                    if (testcontext) return null;

                    if (this.ruleData!.expressionValueType.type !== DataTypes.BOOLEAN) {
                        return (
                            <RuleChangeToggleButtonGroup
                                value={this.ruleData!.ifThenStyle}
                                exclusive
                                onChange={this.changeStyle}
                                aria-label="rule style"
                                size="small"
                            >
                                <ToggleButton
                                    value={IfThenStyle.DATA_RETURN}
                                    aria-label={IfThenStyle.DATA_RETURN}
                                >
                                    {this.ruleData!.ifThenStyle !== IfThenStyle.DATA_RETURN ? (
                                        <LeftAlignIcon alt={IfThenStyle.DATA_RETURN} />
                                    ) : (
                                        <ActiveLeftAlignIcon alt={IfThenStyle.DATA_RETURN} />
                                    )}
                                </ToggleButton>
                                <ToggleButton
                                    value={IfThenStyle.BRANCH_DATA_RETURN}
                                    aria-label="Branch Data Return"
                                >
                                    {this.ruleData!.ifThenStyle !== IfThenStyle.BRANCH_DATA_RETURN ? (
                                        <RightAlignIcon alt={IfThenStyle.BRANCH_DATA_RETURN} />
                                    ) : (
                                        <ActiveRightAlignIcon alt={IfThenStyle.BRANCH_DATA_RETURN} />
                                    )}
                                </ToggleButton>
                            </RuleChangeToggleButtonGroup>
                        );
                    } else {
                        return (
                            <RuleChangeToggleButtonGroup
                                value={this.ruleData!.ifThenStyle}
                                exclusive
                                onChange={this.changeStyle}
                                aria-label="rule style"
                                size="small"
                            >
                                <ToggleButton
                                    value={IfThenStyle.DATA_RETURN}
                                    aria-label={IfThenStyle.DATA_RETURN}
                                >
                                    {this.ruleData!.ifThenStyle !== IfThenStyle.DATA_RETURN ? (
                                        <LeftAlignIcon alt={IfThenStyle.DATA_RETURN} />
                                    ) : (
                                        <ActiveLeftAlignIcon alt={IfThenStyle.DATA_RETURN} />
                                    )}
                                </ToggleButton>
                                <ToggleButton
                                    value={IfThenStyle.BRANCH_DATA_RETURN}
                                    aria-label="Branch Data Return"
                                >
                                    {this.ruleData!.ifThenStyle !== IfThenStyle.BRANCH_DATA_RETURN ? (
                                        <RightAlignIcon alt="Branch Data Return" />
                                    ) : (
                                        <ActiveRightAlignIcon alt="Branch Data Return" />
                                    )}
                                </ToggleButton>
                                <ToggleButton
                                    value={IfThenStyle.LOGICAL_RETURN}
                                    aria-label="Logical Return"
                                >
                                    {this.ruleData!.ifThenStyle !== IfThenStyle.LOGICAL_RETURN ? (
                                        <LogicalIcon alt="Logical Return" />
                                    ) : (
                                        <ActiveLogicalIcon alt="Logical Return" />
                                    )}
                                </ToggleButton>
                            </RuleChangeToggleButtonGroup>
                        );
                    }
                }}
            </TestContext.Consumer>
        );
    }

    @AutoBind
    private changeStyle(event: React.MouseEvent<HTMLElement>, value: IfThenStyle) {
        event.stopPropagation();
        this.ruleData!.ifThenStyle = value;
        StateManager.changed(this);
    }
}

const AllIfDiv = styled.div`
    font-size: 18px;
    width: 100%;
`;

const StyledIfSection = styled.div<{ renderType: ExpressionRenderType; bracketcolor?: string }>`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    width: 100%;

    ${(props) =>
        props.renderType === ExpressionRenderType.FULL
            ? `
            position: relative;
            margin: 2px 0;
            padding-left: 8px;

            &::before {
                content: '';
                width: 4px;
                position: absolute;
                top: 0;
                left: 0;
                bottom: 0;
                border: 1px solid ${props.bracketcolor || UIStyleConstants.BLUE_LIGHTER_COLOR};
                border-right: none;
                border-top-left-radius: 3px;
                border-bottom-left-radius: 3px;
            }
        
            &::after {
                content: '';
                width: 4px;
                position: absolute;
                top: 0;
                right: 0;
                bottom: 0;
                border: 1px solid ${props.bracketcolor || UIStyleConstants.BLUE_LIGHTER_COLOR};
                border-left: none;
                border-top-right-radius: 3px;
                border-bottom-right-radius: 3px;
            }
        `
            : ``}
`;

export const IfSection = (props: { children?: AppGlueAny }) => {
    const context = React.useContext(ExpressionReactContext);
    const colorcontext = React.useContext(ExpressionTestResultColorContext);

    return (
        <StyledIfSection
            {...props}
            renderType={context.renderType}
            bracketcolor={colorcontext}
        />
    );
};

const StyledIfIndentedSection = styled.div<{ renderType: ExpressionRenderType; children?: AppGlueAny }>`
    display: flex;
    align-items: center;
    width: 100%;
    flex-wrap: wrap;
    margin-left: 15px;

    ${(props) =>
        props.renderType === ExpressionRenderType.FULL
            ? `
            padding-left: 13px;
            margin-right: 15px;
            margin-top: 5px;
            margin-bottom: 10px;
            position: relative;
        
            &::before {
                content: '';
                width: 4px;
                position: absolute;
                top: 0;
                left: 0;
                bottom: 0;
                border: 1px solid  ${UIStyleConstants.GRAY_COLOR};
                border-right: none;
                border-top-left-radius: 3px;
                border-bottom-left-radius: 3px;
            }
        
            &::after {
                content: '';
                width: 4px;
                position: absolute;
                top: 0;
                right: 0;
                bottom: 0;
                border: 1px solid  ${UIStyleConstants.GRAY_COLOR};
                border-left: none;
                border-top-right-radius: 3px;
                border-bottom-right-radius: 3px;
            }
        `
            : ``}
`;

export const IfIndentedSection = (props: { children?: AppGlueAny }) => {
    const context = React.useContext(ExpressionReactContext);
    return (
        <StyledIfIndentedSection
            {...props}
            renderType={context.renderType}
        />
    );
};

const StyledActionButtons = styled.div<{ renderType: ExpressionRenderType; children?: AppGlueAny }>`
    ${(props) =>
        props.renderType === ExpressionRenderType.FULL
            ? `
                    display: flex;
                    margin-left: auto;
                    justify-content: flex-end;
                    padding-right: 15px;
                    align-items: center;
                `
            : `display: none;`}
`;

export const ActionButtons = (props: { children?: AppGlueAny }) => {
    const context = React.useContext(ExpressionReactContext);
    return (
        <StyledActionButtons
            {...props}
            renderType={context.renderType}
        />
    );
};

const StyledFloatRight = styled.div<{ renderType: ExpressionRenderType; children?: AppGlueAny }>`
    ${(props) =>
        props.renderType === ExpressionRenderType.FULL
            ? `
                display: flex;
                margin-left: auto;
                justify-content: flex-end;
                padding-right: 15px;
                padding-top: 5px;
                padding-bottom: 5px;
                align-items: center;
                align-self: self-start;
            `
            : `
                display: none;
            `}
`;

export const FloatRight = (props: { children?: AppGlueAny }) => {
    const context = React.useContext(ExpressionReactContext);
    return (
        <StyledFloatRight
            {...props}
            renderType={context.renderType}
        />
    );
};

const TestValueRender = styled.div<{ color: string | undefined }>`
    font-size: 12px;
    position: absolute;

    right: 10px;
    bottom: -16px;

    ${(props) => (props.color ? `color: ${props.color};` : ``)}
`;

const ResultSection = styled.div<{ bracketcolor?: string; renderType: ExpressionRenderType }>`
    display: inline-flex;
    position: relative;
    padding: 5px 10px;
    text-align: center;

    ${(props) =>
        props.renderType === ExpressionRenderType.FULL
            ? `
                &::before {
                    content: "";
                    width: 4px;
                    position: absolute;
                    top: 0;
                    left: 0;
                    bottom: 0;
                    border: 1px solid ${props.bracketcolor || UIStyleConstants.BLUE_LIGHTER_COLOR};
                    border-right: none;
                    border-top-left-radius: 3px;
                    border-bottom-left-radius: 3px;
                }
            
                &::after {
                    content: "";
                    width: 4px;
                    position: absolute;
                    top: 0;
                    right: 0;
                    bottom: 0;
                    border: 1px solid ${props.bracketcolor || UIStyleConstants.BLUE_LIGHTER_COLOR};
                    border-left: none;
                    border-top-right-radius: 3px;
                    border-bottom-right-radius: 3px;
                }
            `
            : `
        `}
`;
